import { api } from './api'
import type {
  MemberDetail,
  MemberImportPreviewResult,
  MemberImportResult,
  MemberImportRowInput,
  MemberListItem,
} from '@riverpark/types'

export interface ListMembersParams {
  page?: number
  limit?: number
  search?: string
  status?: 'active' | 'inactive' | 'invited'
  role?: 'admin' | 'committee' | 'estate_manager' | 'member'
}

export interface ListMembersResponse {
  data: MemberListItem[]
  meta: {
    page: number
    limit: number
    total: number
    totalPages: number
  }
}

export interface PlotOption {
  id: string
  oldNumber: string
  newNumber: string
  status: string
}

export interface UpdateMemberInput {
  name?: string
  email?: string
  phone?: string
  role?: 'admin' | 'committee' | 'estate_manager' | 'member'
  status?: 'active' | 'inactive'
  plotIds?: string[]
}

export async function listMembers(params: ListMembersParams = {}) {
  const qs = new URLSearchParams()
  if (params.page) qs.set('page', String(params.page))
  if (params.limit) qs.set('limit', String(params.limit))
  if (params.search) qs.set('search', params.search)
  if (params.status) qs.set('status', params.status)
  if (params.role) qs.set('role', params.role)
  const query = qs.toString()
  return api.get<ListMembersResponse>(`/members${query ? `?${query}` : ''}`)
}

export async function getMember(id: string) {
  return api.get<{ data: MemberDetail }>(`/members/${id}`)
}

export async function getMyProfile() {
  return api.get<{ data: MemberDetail }>('/members/me')
}

export async function requestPhoneChange(newPhone: string) {
  return api.post<{
    message: string
    smsDelivery?: 'sent' | 'failed' | 'skipped' | 'not_sent' | 'logged_only'
    smsError?: string
    devOtp?: string
    atSandbox?: boolean
  }>('/members/me/phone-change/request', { newPhone })
}

export async function verifyPhoneChange(newPhone: string, code: string) {
  return api.post<{ verified: true; phone: string; expiresAt: number }>(
    '/members/me/phone-change/verify',
    { newPhone, code },
  )
}

export async function requestEmailChange(newEmail: string) {
  return api.post<{
    message: string
    emailDelivery?: 'sent' | 'failed' | 'logged_only'
    emailError?: string
    devOtp?: string
  }>('/members/me/email-change/request', { newEmail })
}

export async function verifyEmailChange(newEmail: string, code: string) {
  return api.post<{ verified: true; email: string; expiresAt: number }>(
    '/members/me/email-change/verify',
    { newEmail, code },
  )
}

export async function updateMember(id: string, body: UpdateMemberInput) {
  return api.patch<{ data: MemberDetail }>(`/members/${id}`, body)
}

/** `reason` is required when the caller is estate_manager — the deactivation is then queued for
 * committee/admin approval instead of executing immediately. */
export async function deactivateMember(id: string, reason?: string) {
  return api.delete<{ data: MemberDetail & { pendingApproval?: unknown } }>(
    `/members/${id}`,
    reason ? { reason } : undefined,
  )
}

export async function inviteMember(body: {
  email: string
  name: string
  phone: string
  role: 'member' | 'committee' | 'estate_manager'
  plotIds?: string[]
  sendSms?: boolean
}) {
  return api.post<{ data: MemberDetail; inviteUrl?: string; expiresInHours: number }>(
    '/members/invite',
    body,
  )
}

export async function resendMemberInvite(id: string) {
  return api.post<{ data: MemberDetail; inviteUrl?: string; expiresInHours: number }>(
    `/members/${id}/resend-invite`,
    {},
  )
}

export async function listPlots() {
  return api.get<{ data: PlotOption[] }>('/plots/options')
}

export async function previewMemberImport(rows: MemberImportRowInput[]) {
  return api.post<{ data: MemberImportPreviewResult }>('/members/import/preview', { rows })
}

export async function confirmMemberImport(rows: MemberImportRowInput[], notify = false) {
  return api.post<{ data: MemberImportResult }>('/members/import', { rows, notify })
}

export async function notifyImportedMembers(memberIds: string[]) {
  return api.post<{
    data: { notified: number; smsSent: number; smsFailed: number; emailsSent: number }
  }>('/members/import/notify', { memberIds })
}
